/* Licenses: every notice Escapade's open content obliges it to show, one section per work.
 *
 * Legal & Credits links here; this page holds the notices themselves. It must stay PUBLIC,
 * bare, and flag-free (index.js routes it like /privacy and /terms): a licence notice
 * behind a login is not a licence notice. Nothing here fetches.
 *
 * Every notice is taken from the work's own legal page, a local copy, never fetched:
 *   SRD 5.2.1  esdrive/content/wotc/SRD_CC_v5.2.1.pdf, page 1
 *   SRD 5.1    esdrive/content/wotc/SRD_CC_v5.1.pdf, page 1
 *   BFRD 1.1   esdrive/content/kobold/Black-Flag-Roleplaying-v05.pdf, page 1
 *   5e-bits    esdrive/content/5e-bits/LICENSE (LICENSE.md at commit bce51b3, LICENSE.url)
 *   Ironsworn, Starforged  https://tomkinpress.com/pages/licensing (read 2026-10-20), its
 *              commercial credit with the game titles filled in
 *   A5E        https://www.a5esrd.com/how-to-use-creative-commons, as es-content-imports.md
 *              §3.3 recorded it (2026-09-29); the site is an import source, never fetched here
 *   Cairn      the CC standard form ("<Work> by <Author> is licensed under ..."):
 *              cairnrpg.com asks for no wording of its own
 *
 * Four rules govern what may be on this page.
 *
 *   1. A notice is reproduced VERBATIM. Each is one string constant, joined from the PDF's
 *      lines with a space between words and none inside a URL the PDF broke across a line,
 *      typographic quotation marks kept, and rendered by esNoticeParts, which cuts it on its
 *      URLs -- never retyped into JSX, where a joined line or a lost space alters it silently.
 *   2. Both SRDs say "Please do not include any other attribution to Wizards". So no logos,
 *      no marks, no "official", no endorsement language, and no Wizards copyright or
 *      rightsholder line of our own invention: their sections carry no Rightsholder line,
 *      because the notice already names the licensor and a second naming is the extra
 *      attribution the SRD asks us not to make. Nothing else in the app attributes Wizards.
 *   3. CC BY 4.0 §3(a)(1)(A) keeps what the licensor supplies: the credit, any copyright
 *      notice (the BFRD's), and the notice referring to the disclaimer of warranties (both
 *      SRDs' "Section 5" sentence). §3(a)(1)(B) wants a notice of modification per work.
 *   4. The Fan Content Policy is deliberately NOT adopted. It forbids login-gated content
 *      and commercial use, which CC BY expressly permits; the app relies on CC BY alone.
 *
 * The BFRD reprints the SRD 5.1 notice on its own page 1. It is drawn once, in the SRD 5.1
 * section, and the BFRD section points at it (es-legal §13).
 *
 * No D&D mark on this page: "5E compatible" is the SRDs' own safe harbour, and the marks'
 * disclaimer is Legal's (es-legal §14). A rulesystem is named by its edition here.
 *
 * NOBODY HERE IS A LAWYER. This is the ordinary form of these notices, not advice.
 */

import { useEsStrings } from '../../common/EsStrings';
import EsMinis from '../../common/EsMinis';
import { EsEnglishGoverns } from './EsPolicy';

const LINK = 'underline decoration-dotted hover:text-accent-hover';
const CC_BY_URL = 'https://creativecommons.org/licenses/by/4.0/legalcode';
const CC_BY = 'Creative Commons Attribution 4.0 International';
// A row's license, by EsNotices' spelling; a row without one is CC BY 4.0.
const LICENSES = {
    'CC-BY-4.0': { url: CC_BY_URL, name: CC_BY },
    'CC-BY-SA-4.0': { url: 'https://creativecommons.org/licenses/by-sa/4.0/legalcode', name: 'Creative Commons Attribution-ShareAlike 4.0 International' },
};
const ORC_URL = 'https://downloads.paizo.com/ORC_License_FINAL.pdf';

// Page 1 of SRD_CC_v5.2.1.pdf, byte for byte. Do not reword, reflow, or interpolate.
export const SRD_ATTRIBUTION =
    'This work includes material from the System Reference Document 5.2.1 ' +
    '(“SRD 5.2.1”) by Wizards of the Coast LLC, available at ' +
    'https://www.dndbeyond.com/srd. The SRD 5.2.1 is licensed under the ' +
    'Creative Commons Attribution 4.0 International License, available at ' +
    'https://creativecommons.org/licenses/by/4.0/legalcode.';

export const SRD_DISCLAIMER =
    'Section 5 of CC-BY-4.0 includes a Disclaimer of Warranties and Limitation of Liability ' +
    'that limits our liability to you.';

export const SRD_MODIFICATION =
    'SRD 5.2.1 content has been reformatted, split into entries, and adapted for this application.';

// Page 1 of SRD_CC_v5.1.pdf. Its disclaimer sentence is SRD_DISCLAIMER word for word.
export const SRD51_ATTRIBUTION =
    'This work includes material taken from the System Reference Document 5.1 (“SRD 5.1”) ' +
    'by Wizards of the Coast LLC and available at ' +
    'https://dnd.wizards.com/resources/systems-reference-document. The SRD 5.1 is licensed ' +
    'under the Creative Commons Attribution 4.0 International License available at ' +
    'https://creativecommons.org/licenses/by/4.0/legalcode.';

export const SRD51_MODIFICATION =
    'SRD 5.1 content has been reformatted, split into entries, and adapted for this application.';

// Page 1 of Black-Flag-Roleplaying-v05.pdf: the credit it asks for ("please credit us as
// follows"), without the "Attribution:" label it prints before it, then its copyright line.
export const BFRD_ATTRIBUTION =
    'This work includes material taken from the Black Flag Reference Document 1.1 (“BFRD 1.1”) ' +
    'by Kobold Press and available at https://koboldpress.com/Black-Flag-Roleplaying. ' +
    'The BFRD 1.1 is licensed under the Creative Commons Attribution 4.0 International ' +
    'License available at https://creativecommons.org/licenses/by/4.0/legalcode.';

export const BFRD_COPYRIGHT = 'Black Flag Roleplaying Reference Document v1.1, © Open Design LLC d/b/a Kobold Press';

export const BFRD_MODIFICATION =
    'BFRD 1.1 content has been reformatted, split into entries, and adapted for this application.';

// a5esrd.com/how-to-use-creative-commons: EN Publishing's own wording, as recorded 2026-09-29 (es-content-imports.md §3.3).
export const A5E_ATTRIBUTION =
    'This work includes material taken from the A5E System Reference Document (A5ESRD) by EN Publishing ' +
    'and available at A5ESRD.com, based on Level Up: Advanced 5th Edition, available at www.levelup5e.com. ' +
    'The A5ESRD is licensed under the Creative Commons Attribution 4.0 International License available at ' +
    'https://creativecommons.org/licenses/by/4.0/legalcode.';

// The CC standard form: cairnrpg.com states the license and asks for no wording of its own.
export const CAIRN_ATTRIBUTION =
    'Cairn Second Edition by Yochai Gal is licensed under CC BY-SA 4.0, ' +
    'https://creativecommons.org/licenses/by-sa/4.0/';

// tomkinpress.com/pages/licensing's commercial credit, its <game titles> filled in.
export const IRONSWORN_ATTRIBUTION =
    'This work is based on Ironsworn and Ironsworn: Delve, created by Shawn Tomkin, and licensed for our use under ' +
    'the Creative Commons Attribution 4.0 International License (https://creativecommons.org/licenses/by/4.0/).';

export const STARFORGED_ATTRIBUTION =
    'This work is based on Ironsworn: Starforged, created by Shawn Tomkin, and licensed for our use under ' +
    'the Creative Commons Attribution 4.0 International License (https://creativecommons.org/licenses/by/4.0/).';

// 5e-bits/5e-database's LICENSE.md at the commit spell-classes.json was derived from,
// byte for byte, its brackets included. One string per paragraph, each joined from the
// file's lines with a space.
export const FIVE_E_BITS_COPYRIGHT = 'Copyright (c) [2018-2020] [Adrian Padua, Christopher Ward]';

export const MIT_TITLE = 'MIT License';

export const MIT_TEXT = [
    'Permission is hereby granted, free of charge, to any person obtaining a copy ' +
    'of this software and associated documentation files (the "Software"), to deal ' +
    'in the Software without restriction, including without limitation the rights ' +
    'to use, copy, modify, merge, publish, distribute, sublicense, and/or sell ' +
    'copies of the Software, and to permit persons to whom the Software is ' +
    'furnished to do so, subject to the following conditions:',
    'The above copyright notice and this permission notice shall be included in all ' +
    'copies or substantial portions of the Software.',
    'THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR ' +
    'IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, ' +
    'FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE ' +
    'AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER ' +
    'LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, ' +
    'OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE ' +
    'SOFTWARE.',
];

/**
 * Cut a notice into alternating text and URL runs, so each URL can be a link and the
 * rendered text is still exactly the notice. Odd indices are the URLs. A URL's closing
 * full stop or parenthesis is the sentence's, not the address's.
 */
export function esNoticeParts( pNotice ) {
    return pNotice.split( /(https?:\/\/[^\s]*[^\s.)])/ );
}

/**
 * esNoticeParts as runs that carry their own key: the kind and where the run starts in the
 * notice. A text run and a URL can repeat, and an empty text run starts where the next URL
 * does, so neither the words nor the place alone is unique; the pair is.
 */
export function esNoticeRuns( pNotice ) {
    let aAt = 0;
    return esNoticeParts( pNotice ).map( ( pPart, pIx ) => {
        const aUrl = pIx % 2 === 1;
        const aRun = { key: ( aUrl ? 'url@' : 'text@' ) + aAt, text: pPart, url: aUrl };
        aAt += pPart.length;
        return aRun;
    } );
}

function EsNotice( { id, notice } ) {
    return (
        <p data-testid={ id } className="mb-3 rounded-sm border border-edge p-5 text-base leading-8">
            { esNoticeRuns( notice ).map( ( pRun ) =>
                pRun.url
                    ? <a key={ pRun.key } href={ pRun.text } target="_blank" rel="noreferrer" className={ LINK }>{ pRun.text }</a>
                    : <span key={ pRun.key }>{ pRun.text }</span>
            ) }
        </p>
    );
}

const Line = ( { id, children } ) => <p data-testid={ id } className="mb-3 text-base leading-8">{ children }</p>;

/**
 * The licensed works, in the order the page draws them.
 *
 * <p>A row's fields are what CC BY asks for: the licence and its link, the notice, any
 * copyright line or disclaimer notice the licensor supplied, the modification notice, and
 * what Escapade uses the work for. `rightsholder` is null for the SRDs (rule 2).
 */
export const LICENSED_WORKS = [
    {
        key: 'srd-5-2-1',
        title: 'System Reference Document 5.2.1',
        notice: SRD_ATTRIBUTION,
        disclaimer: SRD_DISCLAIMER,
        modification: SRD_MODIFICATION,
        rightsholder: null,
        use: 'The codex: its rules, classes, species, backgrounds, feats, spells, monsters, and magic items, and the 2024 fifth-edition rulesystem.',
    },
    {
        key: 'srd-5-1',
        title: 'System Reference Document 5.1',
        notice: SRD51_ATTRIBUTION,
        disclaimer: SRD_DISCLAIMER,
        modification: SRD51_MODIFICATION,
        rightsholder: null,
        use: 'The 2014 fifth-edition rulesystem and its pack: races, classes, spells, monsters, magic items, and the rules chapters.',
    },
    {
        key: 'bfrd',
        title: 'Black Flag Reference Document 1.1',
        notice: BFRD_ATTRIBUTION,
        copyright: BFRD_COPYRIGHT,
        modification: BFRD_MODIFICATION,
        rightsholder: 'Open Design LLC d/b/a Kobold Press',
        includes: 'srd-5-1',
        use: 'The Black Flag rulesystem and its pack: lineages, heritages, classes, spells, monsters, and magic items.',
    },
    {
        key: 'a5e',
        title: 'Level Up Advanced 5th Edition System Reference Document',
        notice: A5E_ATTRIBUTION,
        modification: 'A5E SRD content has been reformatted, split into entries, and adapted for this application.',
        rightsholder: 'EN Publishing',
        use: 'The Level Up A5E rulesystem and its pack: spells, monsters, magic items, feats, backgrounds, and conditions.',
    },
    {
        key: 'cairn-2e',
        title: 'Cairn Second Edition',
        license: 'CC-BY-SA-4.0',
        notice: CAIRN_ATTRIBUTION,
        modification: 'Cairn 2e content has been reformatted, split into entries, and adapted for this application.',
        rightsholder: 'Yochai Gal',
        use: 'The Cairn rulesystem and its pack: the Player\'s Guide, the Warden\'s Guide, and the backgrounds.',
    },
    {
        key: 'ironsworn',
        title: 'Ironsworn and Ironsworn: Delve',
        notice: IRONSWORN_ATTRIBUTION,
        modification: 'Ironsworn content has been reformatted, split into entries, and adapted for this application.',
        rightsholder: 'Shawn Tomkin',
        use: 'The Ironsworn rulesystem and its pack: moves, oracles, assets, and Delve site domains and themes.',
    },
    {
        key: 'starforged',
        title: 'Ironsworn: Starforged',
        notice: STARFORGED_ATTRIBUTION,
        modification: 'Starforged content has been reformatted, split into entries, and adapted for this application.',
        rightsholder: 'Shawn Tomkin',
        use: 'The Starforged rulesystem and its pack: moves, oracles, assets, sample NPCs, and truths.',
    },
];

function EsLicensedWork( { work } ) {
    const t = useEsStrings();
    const aId = 'es-licenses-' + work.key;
    const aLicense = LICENSES[ work.license ?? 'CC-BY-4.0' ];
    const aUse = {
        'srd-5-2-1': t( 'legal-licenses-use-srd-5-2-1', 'The codex: its rules, classes, species, backgrounds, feats, spells, monsters, and magic items, and the 2024 fifth-edition rulesystem.' ),
        'srd-5-1': t( 'legal-licenses-use-srd-5-1', 'The 2014 fifth-edition rulesystem and its pack: races, classes, spells, monsters, magic items, and the rules chapters.' ),
        bfrd: t( 'legal-licenses-use-bfrd', 'The Black Flag rulesystem and its pack: lineages, heritages, classes, spells, monsters, and magic items.' ),
    }[ work.key ] ?? work.use;

    return (
        <div id={ aId } data-testid={ aId } className="mb-10">
            <h4 data-testid={ aId + '-heading' } className="mb-3 text-lg font-bold text-heading">{ work.title }</h4>
            <Line id={ aId + '-license' }>
                { t( 'legal-licenses-license', 'License:' ) } <a href={ aLicense.url } target="_blank" rel="noreferrer" className={ LINK }>{ aLicense.name }</a>
            </Line>
            <EsNotice id={ aId + '-attribution' } notice={ work.notice } />
            { work.copyright && <Line id={ aId + '-copyright' }>{ work.copyright }</Line> }
            { work.disclaimer && <Line id={ aId + '-disclaimer' }>{ work.disclaimer }</Line> }
            { work.includes && (
                <Line id={ aId + '-includes' }>
                    { t( 'legal-licenses-includes', 'It includes SRD 5.1 material, credited under' ) } <a href={ '#es-licenses-' + work.includes } className={ LINK }>
                        { /* es-copy: literal -- the work's own name */ }
                        System Reference Document 5.1
                    </a> { t( 'legal-licenses-includes-above', 'above.' ) }
                </Line>
            ) }
            { work.rightsholder && <Line id={ aId + '-rightsholder' }>{ t( 'legal-licenses-rightsholder', 'Rightsholder:' ) } { work.rightsholder }</Line> }
            <Line id={ aId + '-modification' }>{ work.modification }</Line>
            <Line id={ aId + '-use' }>{ t( 'legal-licenses-used-for', 'Used for:' ) } { aUse }</Line>
        </div>
    );
}

export default function EsLicenses() {
    const t = useEsStrings();
    return (
        <section data-testid="es-licenses-page" className="relative z-10 pb-4 pt-nav-room">
            <EsMinis />
            <div className="container">
                <div className="mx-auto px-4 xl:w-[800px] max-sm:break-words">
                    <h3 data-testid="es-licenses-heading" className="mb-3 text-center text-2xl font-bold text-heading sm:text-3xl">
                        { t( 'legal-licenses-heading', 'Licenses' ) }
                    </h3>
                    <p data-testid="es-licenses-blurb" className="mb-10 text-center opacity-70">
                        { t( 'legal-licenses-blurb', 'The open content Escapade uses, who made it, and the terms it comes under.' ) }
                    </p>
                    <EsEnglishGoverns id="es-licenses" />

                    { LICENSED_WORKS.map( ( pWork ) => <EsLicensedWork key={ pWork.key } work={ pWork } /> ) }

                    { /* No ORC work is imported yet; this states the share-alike rule Tim accepted
                         (2026-10-14). When one is, add its ORC Notice, Attribution Notice, Reserved
                         Material Notice, and Expressly Designated statement here, one block each,
                         verbatim from the work's own legal page (ORC §III). Name no Paizo work
                         before then. */ }
                    <div data-testid="es-licenses-orc" className="mb-10">
                        { /* es-copy: literal -- a license or work's own name, and its notice verbatim */ }
                        <h4 data-testid="es-licenses-orc-heading" className="mb-3 text-lg font-bold text-heading">ORC License</h4>
                        <Line id="es-licenses-orc-license">
                            { t( 'legal-licenses-license', 'License:' ) } <a href={ ORC_URL } target="_blank" rel="noreferrer" className={ LINK }>
                                { /* es-copy: literal */ }
                                ORC License
                            </a>
                        </Line>
                        <Line id="es-licenses-orc-share-alike">
                            { t( 'legal-licenses-orc-share-alike', 'Any rules Escapade adapts from ORC-licensed material are offered back under the ORC License.' ) }
                        </Line>
                        <Line id="es-licenses-orc-closed">
                            { t( 'legal-licenses-orc-closed', 'Escapade\'s own code and lore are not ORC material. The ORC License does not reach software code.' ) }
                        </Line>
                    </div>

                    { /* The copyright line is the font's own (its name table, nunito-latin.woff2);
                         its licence field points at the SIL Open Font License 1.1. */ }
                    <div data-testid="es-licenses-nunito" className="mb-10">
                        <h4 data-testid="es-licenses-nunito-heading" className="mb-3 text-lg font-bold text-heading">{ t( 'legal-licenses-nunito', 'Nunito typeface' ) }</h4>
                        <Line id="es-licenses-nunito-license">
                            { t( 'legal-licenses-license', 'License:' ) } <a href="https://openfontlicense.org" target="_blank" rel="noreferrer" className={ LINK }>
                                { /* es-copy: literal */ }
                                SIL Open Font License 1.1
                            </a>
                        </Line>
                        { /* es-copy: literal -- the font's own copyright line */ }
                        <Line id="es-licenses-nunito-copyright">Copyright 2014 The Nunito Project Authors (https://github.com/googlefonts/nunito)</Line>
                        <Line id="es-licenses-nunito-use">{ t( 'legal-licenses-used-for', 'Used for:' ) } { t( 'legal-licenses-use-nunito', 'the site\'s text.' ) }</Line>
                    </div>

                    { /* The clay figures behind the home page and the sparse pages (EsMinis.js) are
                         renders of MZ4250's miniature models, which he publishes under CC BY 4.0; the
                         credit is the attribution that license asks for (es-license-register.md). */ }
                    <div data-testid="es-licenses-mz4250" className="mb-10">
                        <h4 data-testid="es-licenses-mz4250-heading" className="mb-3 text-lg font-bold text-heading">{ t( 'legal-licenses-mz4250', 'MZ4250 miniatures' ) }</h4>
                        <Line id="es-licenses-mz4250-license">
                            { t( 'legal-licenses-license', 'License:' ) } <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noreferrer" className={ LINK }>
                                { /* es-copy: literal */ }
                                CC BY 4.0
                            </a>
                        </Line>
                        <Line id="es-licenses-mz4250-credit">
                            { t( 'legal-licenses-mz4250-credit', 'Miniature models by Miguel Zavala (MZ4250),' ) } <a href="https://www.patreon.com/mz4250" target="_blank" rel="noreferrer" className={ LINK }>
                                { /* es-copy: literal */ }
                                patreon.com/mz4250
                            </a>
                        </Line>
                        <Line id="es-licenses-mz4250-use">{ t( 'legal-licenses-used-for', 'Used for:' ) } { t( 'legal-licenses-use-mz4250', 'the clay figures drawn behind the home page and other pages.' ) }</Line>
                    </div>

                    { /* The sample maps every table is given are Dyson Logos's commercial maps, which
                         he offers free for personal and commercial use with credit; the credit is the
                         one his commercial-maps page asks for. */ }
                    <div data-testid="es-licenses-dyson-logos" className="mb-10">
                        <h4 data-testid="es-licenses-dyson-logos-heading" className="mb-3 text-lg font-bold text-heading">{ t( 'legal-licenses-dyson-logos', 'Dyson Logos maps' ) }</h4>
                        <Line id="es-licenses-dyson-logos-license">
                            { t( 'legal-licenses-license', 'License:' ) } <a href="https://dysonlogos.blog/maps/commercial-maps/" target="_blank" rel="noreferrer" className={ LINK }>
                                { t( 'legal-licenses-dyson-logos-license', 'free for personal and commercial use with credit' ) }
                            </a>
                        </Line>
                        <Line id="es-licenses-dyson-logos-credit">
                            { t( 'legal-licenses-dyson-logos-credit', 'Maps by Dyson Logos,' ) } <a href="https://dysonlogos.blog/" target="_blank" rel="noreferrer" className={ LINK }>
                                { /* es-copy: literal */ }
                                dysonlogos.blog
                            </a>
                        </Line>
                        <Line id="es-licenses-dyson-logos-use">{ t( 'legal-licenses-used-for', 'Used for:' ) } { t( 'legal-licenses-use-dyson-logos', 'the sample maps every table is given.' ) }</Line>
                    </div>

                    { /* MIT asks for its copyright notice and permission notice with every copy;
                         the licence is collapsed under its title, as EsOgl.js does the OGL. */ }
                    <div data-testid="es-licenses-5e-bits" className="mb-10">
                        { /* es-copy: literal -- the work's own name */ }
                        <h4 data-testid="es-licenses-5e-bits-heading" className="mb-3 text-lg font-bold text-heading">5e-bits 5e-database</h4>
                        <Line id="es-licenses-5e-bits-license">
                            { t( 'legal-licenses-license', 'License:' ) } <a href="https://opensource.org/license/mit" target="_blank" rel="noreferrer" className={ LINK }>{ MIT_TITLE }</a>
                        </Line>
                        <Line id="es-licenses-5e-bits-copyright">{ FIVE_E_BITS_COPYRIGHT }</Line>
                        <details data-testid="es-licenses-5e-bits-text" className="mb-3 rounded-sm border border-edge p-5 text-sm leading-7">
                            <summary className="cursor-pointer font-semibold">{ MIT_TITLE }</summary>
                            <p className="mt-4 mb-4">{ FIVE_E_BITS_COPYRIGHT }</p>
                            { MIT_TEXT.map( ( pText ) => <p key={ pText.slice( 0, 40 ) } className="mb-4">{ pText }</p> ) }
                        </details>
                        <Line id="es-licenses-5e-bits-use">{ t( 'legal-licenses-used-for', 'Used for:' ) } { t( 'legal-licenses-use-5e-bits', 'spell class lists' ) }</Line>
                    </div>

                    { /* 13th Age Archmage Engine (OGL 1.0a), pending Tim's OGL decision (es-legal §6).
                         Built and not drawn: EsOgl.js holds the section, the footer notice verbatim,
                         Fire Opal Media as rightsholder, the Product Identity left out, and the full
                         OGL text. Draw it once 13th Age content is imported and Pelgrane's §15 page
                         has been read; see the top of EsOgl.js.
                    <Es13thAge />
                    */ }
                </div>
            </div>
        </section>
    );
}
